import { useState, useCallback, useEffect, useRef } from "react";
import type { CriteriaId, CriteriaListItem } from "../types/meeting.type";

interface UseCriteriaNavigationOptions {
  criteriaList: CriteriaListItem[];
}

interface UseCriteriaNavigationResult {
  currentCriteriaId: CriteriaId;
  goNext: () => void;
  goPrev: () => void;
  goTo: (id: CriteriaId) => void;
}

const findFirstIncompleteCriteria = (criteriaList: CriteriaListItem[]): CriteriaId => {
  const firstIncomplete = criteriaList.find((c) => !c.is_complete);
  if (firstIncomplete) {
    return firstIncomplete.id;
  }
  if (criteriaList.length > 0) {
    return criteriaList[0].id;
  }
  return 'overall';
};

export const useCriteriaNavigation = ({
  criteriaList,
}: UseCriteriaNavigationOptions): UseCriteriaNavigationResult => {
  const [currentCriteriaId, setCurrentCriteriaId] = useState<CriteriaId>(() =>
    findFirstIncompleteCriteria(criteriaList)
  );

  // When criteria list first populates (empty → non-empty), navigate to first incomplete
  const hasInitialized = useRef(criteriaList.length > 0);
  useEffect(() => {
    if (!hasInitialized.current && criteriaList.length > 0) {
      hasInitialized.current = true;
      setCurrentCriteriaId(findFirstIncompleteCriteria(criteriaList));
    }
  }, [criteriaList]);

  const numericIds = criteriaList.map((c) => c.id);

  const goNext = useCallback(() => {
    if (currentCriteriaId === 'overall') {
      return;
    }
    const currentIndex = numericIds.indexOf(currentCriteriaId);
    if (currentIndex === -1) {
      return;
    }
    if (currentIndex < numericIds.length - 1) {
      setCurrentCriteriaId(numericIds[currentIndex + 1]);
    } else {
      setCurrentCriteriaId('overall');
    }
  }, [currentCriteriaId, numericIds]);

  const goPrev = useCallback(() => {
    if (currentCriteriaId === 'overall') {
      if (numericIds.length > 0) {
        setCurrentCriteriaId(numericIds[numericIds.length - 1]);
      }
      return;
    }
    const currentIndex = numericIds.indexOf(currentCriteriaId);
    if (currentIndex <= 0) {
      return;
    }
    setCurrentCriteriaId(numericIds[currentIndex - 1]);
  }, [currentCriteriaId, numericIds]);

  const goTo = useCallback((id: CriteriaId) => {
    setCurrentCriteriaId(id);
  }, []);

  return {
    currentCriteriaId,
    goNext,
    goPrev,
    goTo,
  };
};
